import { z } from 'zod'

// ============================================================================
// Enums
// ============================================================================

export const UserStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'DEACTIVATED'])
export const UserThemeSchema = z.enum(['light', 'dark', 'system'])

// ============================================================================
// User Schemas
// ============================================================================

/**
 * Schema pour un utilisateur de base
 */
export const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().url().nullable().optional(),
  establishmentId: z.string().uuid().nullable(),
  status: UserStatusSchema,
  locale: z.string(),
  theme: z.string(),
  lastSeenAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export const usersSchema = z.array(userSchema)

// ============================================================================
// Form Schemas (validation)
// ============================================================================

/**
 * Schema pour créer un utilisateur
 */
export const createUserSchema = z.object({
  id: z.string().uuid('ID invalide'),
  email: z.string().email('Email invalide').optional(),
  username: z.string().min(1, 'Username requis').max(255).optional(),
  firstName: z.string().max(255).optional(),
  lastName: z.string().max(255).optional(),
  establishmentId: z.string().uuid().optional(),
  status: UserStatusSchema.optional(),
})

/**
 * Schema pour mettre à jour un utilisateur (admin)
 */
export const updateUserSchema = z.object({
  email: z.string().email('Email invalide').optional(),
  username: z.string().min(1).max(255).optional(),
  firstName: z.string().max(255).optional(),
  lastName: z.string().max(255).optional(),
  displayName: z.string().max(255).optional(),
  locale: z.string().max(16).optional(),
  timezone: z.string().max(64).optional(),
})

/**
 * Schema pour mettre à jour son profil
 */
export const updateProfileSchema = z.object({
  displayName: z.string().max(255).optional(),
  avatarUrl: z.string().url('URL invalide').optional().or(z.literal('')),
  locale: z.string().max(16).optional(),
  timezone: z.string().max(64).optional(),
  theme: UserThemeSchema.optional(),
  birthDate: z.string().datetime().optional().or(z.literal('')),
  gender: z.string().optional(),
  spokenLanguages: z.array(z.string()).optional(),
})

/**
 * Schema pour suspendre un utilisateur
 */
export const suspendUserSchema = z.object({
  reason: z.string().max(500, 'Raison trop longue').optional(),
})

// ============================================================================
// Inferred Types
// ============================================================================

export type UserSchema = z.infer<typeof userSchema>
export type CreateUserFormData = z.infer<typeof createUserSchema>
export type UpdateUserFormData = z.infer<typeof updateUserSchema>
export type UpdateProfileFormData = z.infer<typeof updateProfileSchema>
export type SuspendUserFormData = z.infer<typeof suspendUserSchema>
